import { useState, useEffect, useMemo, useCallback, useRef, Fragment } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../components/ui/hover-card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import TournamentSelector from '../components/TournamentSelector';
import {
  Plus,
  Trash2,
  Edit,
  IndianRupee,
  FileText,
  Wallet,
  TrendingUp,
  CalendarClock,
  Users2,
  Receipt as ReceiptIcon,
  AlertCircle,
  ChevronRight,
  Mail,
  Download,
} from 'lucide-react';

import { exportNodeToPdf, exportCardsToPdf } from '../lib/pdfExport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatINR = (amount) =>
  `₹${Number(amount || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (d) => {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return d;
  }
};

const daysUntil = (d) => {
  if (!d) return null;
  const target = new Date(d);
  const today = new Date();
  target.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
};

const statusBadge = (status) => {
  const map = {
    draft: { label: 'Draft', variant: 'secondary' },
    confirmed: { label: 'Confirmed', variant: 'default' },
    partially_invoiced: { label: 'Partially Invoiced', variant: 'secondary' },
    fully_invoiced: { label: 'Fully Invoiced', variant: 'default' },
    completed: { label: 'Completed', variant: 'success' },
    pending: { label: 'Pending', variant: 'secondary' },
    partially_paid: { label: 'Partially Paid', variant: 'secondary' },
    paid: { label: 'Paid', variant: 'success' },
  };
  const item = map[status] || { label: status, variant: 'secondary' };
  return <Badge variant={item.variant} data-testid={`status-${status}`}>{item.label}</Badge>;
};

const Revenue = () => {
  const [tournament, setTournament] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('team-overview');
  // When the Accounting tab deep-links here with ?highlight=<receipt_id>,
  // auto-switch to the receipts sub-tab and scroll to that row.
  const location = useLocation();
  const highlightId = new URLSearchParams(location.search).get('highlight') || '';
  useEffect(() => {
    if (highlightId) {
      setActiveTab('receipts');
      // give the table a tick to render before scrolling
      setTimeout(() => {
        const el = document.getElementById(`receipt-${highlightId}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 400);
    }
  }, [highlightId]);

  // Data
  const [overview, setOverview] = useState(null);
  const [salesOrders, setSalesOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [receipts, setReceipts] = useState([]);

  // Team Overview filters
  const [teamSearch, setTeamSearch] = useState('');
  const [teamSort, setTeamSort] = useState('outstanding');
  const [teamFilter, setTeamFilter] = useState('all');
  const teamOverviewRef = useRef(null);
  const [teamPdfExporting, setTeamPdfExporting] = useState(false);

  const handleExportTeamOverview = async () => {
    if (!teamOverviewRef.current) return;
    setTeamPdfExporting(true);
    try {
      // Capture each team card independently so no card is split across page
      // boundaries. The PDF exporter will pack cards into a 2-column landscape
      // layout and flow overflow to a new page when the current page is full.
      const cards = Array.from(
        teamOverviewRef.current.querySelectorAll('[data-testid^="team-progress-"]')
      );
      if (cards.length === 0) {
        toast.error('No teams to export');
        return;
      }
      await exportCardsToPdf(cards, {
        filename: `MPL_Team_Overview_${(tournament || 'all').replace(/[^A-Za-z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}`,
        title: 'MPL Finance — Team Overview',
        subtitle: `${tournament || 'All tournaments'} · ${(filteredSortedSOs || []).length} teams`,
        columns: 2,
      });
      toast.success('Team Overview PDF exported');
    } catch (e) {
      toast.error(e.message || 'Export failed');
    } finally {
      setTeamPdfExporting(false);
    }
  };

  // Reminder dialog
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [reminderTarget, setReminderTarget] = useState(null);
  const [reminderEmail, setReminderEmail] = useState('');
  const [reminderMessage, setReminderMessage] = useState('');
  const [reminderSending, setReminderSending] = useState(false);

  const openReminderDialog = (milestone) => {
    setReminderTarget(milestone);
    setReminderEmail('');
    setReminderMessage('');
    setReminderDialogOpen(true);
  };

  const sendReminder = async (e) => {
    e.preventDefault();
    if (!reminderTarget) return;
    setReminderSending(true);
    try {
      await axios.post(
        `${API}/revenue/milestones/remind`,
        {
          so_id: reminderTarget.so_id,
          milestone_index: reminderTarget.idx,
          recipient_email: reminderEmail,
          custom_message: reminderMessage || undefined,
        },
        { withCredentials: true }
      );
      toast.success(`Reminder sent to ${reminderEmail}`);
      setReminderDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setReminderSending(false);
    }
  };

  // Dialogs
  const [soDialogOpen, setSoDialogOpen] = useState(false);
  const [editingSO, setEditingSO] = useState(null);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [expandedSO, setExpandedSO] = useState(null);

  // SO Form
  const emptySOForm = {
    so_number: '',
    team_name: '',
    category: 'Sponsorship',
    total_amount: '',
    taxable_amount: '',
    cgst: '0',
    sgst: '0',
    igst: '0',
    tds: '0',
    status: 'draft',
    notes: '',
    tentative_dates: [],
  };
  const [soForm, setSoForm] = useState(emptySOForm);

  // Invoice Form
  const [invoiceForm, setInvoiceForm] = useState({
    invoice_number: '',
    sales_order_id: '',
    invoice_date: new Date().toISOString().split('T')[0],
    amount: '',
    tax_amount: '0',
    taxable_amount: '',
    cgst: '0',
    sgst: '0',
    igst: '0',
    tds: '0',
    due_date: '',
    notes: '',
  });

  // Receipt Form
  const [receiptForm, setReceiptForm] = useState({
    receipt_number: '',
    invoice_id: '',
    receipt_date: new Date().toISOString().split('T')[0],
    amount: '',
    taxable_amount: '',
    cgst: '0',
    sgst: '0',
    igst: '0',
    tds: '0',
    payment_method: 'Bank Transfer',
    reference_number: '',
    remarks: '',
  });

  const fetchAll = useCallback(async () => {
    if (!tournament) return;
    setLoading(true);
    try {
      const [soRes, invRes, recRes, overviewRes] = await Promise.all([
        axios.get(`${API}/revenue/sales-orders`, {
          params: { tournament },
          withCredentials: true,
        }),
        axios.get(`${API}/revenue/invoices`, { withCredentials: true }),
        axios.get(`${API}/revenue/receipts`, { withCredentials: true }),
        axios.get(`${API}/revenue/analytics/overview`, {
          params: { tournament },
          withCredentials: true,
        }),
      ]);
      setSalesOrders(soRes.data.sales_orders || []);
      setInvoices(invRes.data.invoices || []);
      setReceipts(recRes.data.receipts || []);
      setOverview(overviewRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load revenue data');
    } finally {
      setLoading(false);
    }
  }, [tournament]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ============== SALES ORDER ==============
  const openSODialog = (so = null) => {
    if (so) {
      setEditingSO(so);
      setSoForm({
        so_number: so.so_number,
        team_name: so.team_name,
        category: so.category || 'Sponsorship',
        total_amount: String(so.total_amount),
        taxable_amount: String(so.taxable_amount ?? so.total_amount ?? ''),
        cgst: String(so.cgst || 0),
        sgst: String(so.sgst || 0),
        igst: String(so.igst || 0),
        tds: String(so.tds || 0),
        status: so.status,
        notes: so.notes || '',
        tentative_dates: so.tentative_dates || [],
      });
    } else {
      setEditingSO(null);
      setSoForm(emptySOForm);
    }
    setSoDialogOpen(true);
  };

  const addMilestone = () => {
    setSoForm({
      ...soForm,
      tentative_dates: [
        ...soForm.tentative_dates,
        { date: '', amount: '', description: '', type: 'payment' },
      ],
    });
  };

  const updateMilestone = (idx, field, value) => {
    const updated = [...soForm.tentative_dates];
    updated[idx] = { ...updated[idx], [field]: value };
    setSoForm({ ...soForm, tentative_dates: updated });
  };

  const removeMilestone = (idx) => {
    setSoForm({
      ...soForm,
      tentative_dates: soForm.tentative_dates.filter((_, i) => i !== idx),
    });
  };

  const handleSubmitSO = async (e) => {
    e.preventDefault();
    if (!tournament) {
      toast.error('Please select a tournament first');
      return;
    }
    try {
      const taxable = parseFloat(soForm.taxable_amount || soForm.total_amount || 0);
      const cgst = parseFloat(soForm.cgst || 0);
      const sgst = parseFloat(soForm.sgst || 0);
      const igst = parseFloat(soForm.igst || 0);
      const tds = parseFloat(soForm.tds || 0);
      const grossTotal = taxable + cgst + sgst + igst;
      const payload = {
        so_number: soForm.so_number,
        team_name: soForm.team_name,
        category: soForm.category || 'Other',
        tournament,
        total_amount: grossTotal || parseFloat(soForm.total_amount || 0),
        taxable_amount: taxable,
        cgst,
        sgst,
        igst,
        tds,
        status: soForm.status,
        notes: soForm.notes,
        tentative_dates: soForm.tentative_dates
          .filter((m) => m.date)
          .map((m) => ({
            date: m.date,
            amount: parseFloat(m.amount || 0),
            description: m.description || '',
            type: m.type || 'payment',
          })),
      };
      if (editingSO) {
        await axios.put(
          `${API}/revenue/sales-orders/${editingSO.id}`,
          payload,
          { withCredentials: true }
        );
        toast.success('Sales order updated');
      } else {
        await axios.post(`${API}/revenue/sales-orders`, payload, {
          withCredentials: true,
        });
        toast.success('Sales order created');
      }
      setSoDialogOpen(false);
      setEditingSO(null);
      setSoForm(emptySOForm);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save sales order');
    }
  };

  const deleteSO = async (id) => {
    try {
      await axios.delete(`${API}/revenue/sales-orders/${id}`, {
        withCredentials: true,
      });
      toast.success('Sales order deleted');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete sales order');
    }
  };

  // ============== INVOICE ==============
  // Fetch the next suggested invoice number for the current tournament. Used
  // both on dialog open (when creating a brand-new invoice) and via a manual
  // "Suggest" button if the user wants to refresh after typing.
  const suggestInvoiceNumber = async (dateStr) => {
    try {
      const res = await axios.get(`${API}/revenue/invoices/suggest-invoice-number`, {
        params: { tournament, date: dateStr || undefined },
        withCredentials: true,
      });
      return res.data?.invoice_number || '';
    } catch (e) {
      console.warn('Revenue: suggest invoice number failed', e?.response?.data || e?.message);
      return '';
    }
  };

  const openInvoiceDialog = async (soId = '', invoice = null) => {
    if (invoice) {
      setEditingInvoice(invoice);
      setInvoiceForm({
        invoice_number: invoice.invoice_number,
        sales_order_id: invoice.sales_order_id,
        invoice_date: invoice.invoice_date
          ? new Date(invoice.invoice_date).toISOString().split('T')[0]
          : '',
        amount: String(invoice.amount || ''),
        tax_amount: String(invoice.tax_amount || 0),
        taxable_amount: String(invoice.taxable_amount ?? invoice.amount ?? ''),
        cgst: String(invoice.cgst || 0),
        sgst: String(invoice.sgst || 0),
        igst: String(invoice.igst || 0),
        tds: String(invoice.tds || 0),
        due_date: invoice.due_date
          ? new Date(invoice.due_date).toISOString().split('T')[0]
          : '',
        notes: invoice.notes || '',
      });
    } else {
      setEditingInvoice(null);
      const today = new Date().toISOString().split('T')[0];
      const suggested = await suggestInvoiceNumber(today);
      setInvoiceForm({
        invoice_number: suggested,
        sales_order_id: soId,
        invoice_date: today,
        amount: '',
        tax_amount: '0',
        taxable_amount: '',
        cgst: '0',
        sgst: '0',
        igst: '0',
        tds: '0',
        due_date: '',
        notes: '',
      });
    }
    setInvoiceDialogOpen(true);
  };

  const handleSubmitInvoice = async (e) => {
    e.preventDefault();
    try {
      const taxable = parseFloat(invoiceForm.taxable_amount || invoiceForm.amount || 0);
      const cgst = parseFloat(invoiceForm.cgst || 0);
      const sgst = parseFloat(invoiceForm.sgst || 0);
      const igst = parseFloat(invoiceForm.igst || 0);
      const tds = parseFloat(invoiceForm.tds || 0);
      const payload = {
        invoice_number: invoiceForm.invoice_number,
        invoice_date: new Date(invoiceForm.invoice_date).toISOString(),
        amount: taxable, // legacy "amount" === taxable basis
        tax_amount: cgst + sgst + igst, // legacy aggregate (kept in sync)
        taxable_amount: taxable,
        cgst,
        sgst,
        igst,
        tds,
        due_date: invoiceForm.due_date
          ? new Date(invoiceForm.due_date).toISOString()
          : null,
        notes: invoiceForm.notes,
      };
      if (editingInvoice) {
        await axios.put(
          `${API}/revenue/invoices/${editingInvoice.id}`,
          payload,
          { withCredentials: true }
        );
        toast.success('Invoice updated');
      } else {
        await axios.post(
          `${API}/revenue/invoices`,
          { ...payload, sales_order_id: invoiceForm.sales_order_id },
          { withCredentials: true }
        );
        toast.success('Invoice created');
      }
      setInvoiceDialogOpen(false);
      setEditingInvoice(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save invoice');
    }
  };

  const deleteInvoice = async (id) => {
    try {
      await axios.delete(`${API}/revenue/invoices/${id}`, {
        withCredentials: true,
      });
      toast.success('Invoice deleted');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  // ============== RECEIPT ==============
  const openReceiptDialog = (invoiceId = '', receipt = null) => {
    if (receipt) {
      setEditingReceipt(receipt);
      setReceiptForm({
        receipt_number: receipt.receipt_number,
        invoice_id: receipt.invoice_id,
        receipt_date: receipt.receipt_date
          ? new Date(receipt.receipt_date).toISOString().split('T')[0]
          : '',
        amount: String(receipt.amount || ''),
        taxable_amount: String(receipt.taxable_amount ?? receipt.amount ?? ''),
        cgst: String(receipt.cgst || 0),
        sgst: String(receipt.sgst || 0),
        igst: String(receipt.igst || 0),
        tds: String(receipt.tds || 0),
        payment_method: receipt.payment_method || 'Bank Transfer',
        reference_number: receipt.reference_number || '',
        remarks: receipt.remarks || '',
      });
    } else {
      setEditingReceipt(null);
      setReceiptForm({
        receipt_number: '',
        invoice_id: invoiceId,
        receipt_date: new Date().toISOString().split('T')[0],
        amount: '',
        taxable_amount: '',
        cgst: '0',
        sgst: '0',
        igst: '0',
        tds: '0',
        payment_method: 'Bank Transfer',
        reference_number: '',
        remarks: '',
      });
    }
    setReceiptDialogOpen(true);
  };

  const handleSubmitReceipt = async (e) => {
    e.preventDefault();
    try {
      const taxable = parseFloat(receiptForm.taxable_amount || receiptForm.amount || 0);
      const cgst = parseFloat(receiptForm.cgst || 0);
      const sgst = parseFloat(receiptForm.sgst || 0);
      const igst = parseFloat(receiptForm.igst || 0);
      const tds = parseFloat(receiptForm.tds || 0);
      const payload = {
        receipt_number: receiptForm.receipt_number,
        receipt_date: new Date(receiptForm.receipt_date).toISOString(),
        amount: parseFloat(receiptForm.amount || 0),
        taxable_amount: taxable,
        cgst,
        sgst,
        igst,
        tds,
        payment_method: receiptForm.payment_method,
        reference_number: receiptForm.reference_number,
        remarks: receiptForm.remarks,
      };
      if (editingReceipt) {
        await axios.put(
          `${API}/revenue/receipts/${editingReceipt.id}`,
          payload,
          { withCredentials: true }
        );
        toast.success('Receipt updated');
      } else {
        await axios.post(
          `${API}/revenue/receipts`,
          { ...payload, invoice_id: receiptForm.invoice_id },
          { withCredentials: true }
        );
        toast.success('Receipt recorded');
      }
      setReceiptDialogOpen(false);
      setEditingReceipt(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save receipt');
    }
  };

  const deleteReceipt = async (id) => {
    try {
      await axios.delete(`${API}/revenue/receipts/${id}`, {
        withCredentials: true,
      });
      toast.success('Receipt deleted');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete receipt');
    }
  };

  // ============== COMPUTED ==============
  const upcomingMilestones = useMemo(() => {
    const items = [];
    salesOrders.forEach((so) => {
      (so.tentative_dates || []).forEach((m, idx) => {
        if (!m.date) return;
        items.push({
          so_id: so.id,
          so_number: so.so_number,
          team_name: so.team_name,
          idx,
          ...m,
          daysUntil: daysUntil(m.date),
        });
      });
    });
    return items
      .filter((m) => m.daysUntil !== null && m.daysUntil >= -7)
      .sort((a, b) => a.daysUntil - b.daysUntil)
      .slice(0, 10);
  }, [salesOrders]);

  // Filter invoices/receipts by tournament (via their SO)
  const soIds = useMemo(() => new Set(salesOrders.map((s) => s.id)), [salesOrders]);
  const tournamentInvoices = useMemo(
    () => invoices.filter((i) => soIds.has(i.sales_order_id)),
    [invoices, soIds]
  );
  const invoiceIds = useMemo(
    () => new Set(tournamentInvoices.map((i) => i.id)),
    [tournamentInvoices]
  );
  const tournamentReceipts = useMemo(
    () => receipts.filter((r) => invoiceIds.has(r.invoice_id)),
    [receipts, invoiceIds]
  );

  // Breakdown of receipts by taxable / TDS / GST for the "Received" KPI card.
  const receivedBreakdown = useMemo(() => {
    let taxable = 0;
    let tds = 0;
    let gst = 0;
    for (const r of tournamentReceipts) {
      taxable += r.taxable_amount || 0;
      tds += r.tds || 0;
      gst += (r.cgst || 0) + (r.sgst || 0) + (r.igst || 0);
    }
    return { taxable, tds, gst };
  }, [tournamentReceipts]);

  // Team Overview: filter + sort
  const filteredSortedSOs = useMemo(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const list = salesOrders
      .map((so) => {
        const total = so.total_amount || 0;
        const invoiced = so.invoiced_amount || 0;
        const received = so.received_amount || 0;
        const outstanding = invoiced - received;
        const milestones = so.tentative_dates || [];
        const dueByToday = milestones.reduce((sum, m) => {
          if (!m.date) return sum;
          return new Date(m.date) <= today ? sum + (parseFloat(m.amount) || 0) : sum;
        }, 0);
        const onTrack = received >= dueByToday;
        const billingPct = total > 0 ? (invoiced / total) * 100 : 0;
        const collectionPct = invoiced > 0 ? (received / invoiced) * 100 : 0;
        return { so, total, invoiced, received, outstanding, onTrack, billingPct, collectionPct };
      })
      .filter(({ so }) => {
        if (!teamSearch) return true;
        const q = teamSearch.toLowerCase();
        return (
          (so.team_name || '').toLowerCase().includes(q) ||
          (so.so_number || '').toLowerCase().includes(q)
        );
      })
      .filter(({ onTrack, outstanding }) => {
        if (teamFilter === 'all') return true;
        if (teamFilter === 'behind') return !onTrack;
        if (teamFilter === 'on_track') return onTrack;
        if (teamFilter === 'outstanding') return outstanding > 0;
        if (teamFilter === 'cleared') return outstanding <= 0;
        return true;
      });

    const sorters = {
      outstanding: (a, b) => b.outstanding - a.outstanding,
      team: (a, b) => (a.so.team_name || '').localeCompare(b.so.team_name || ''),
      contract: (a, b) => b.total - a.total,
      billing: (a, b) => a.billingPct - b.billingPct,
      collection: (a, b) => a.collectionPct - b.collectionPct,
    };
    list.sort(sorters[teamSort] || sorters.outstanding);
    return list.map((x) => x.so);
  }, [salesOrders, teamSearch, teamSort, teamFilter]);

  return (
    <div data-testid="revenue-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">
            Revenue
          </h1>
          <p className="text-muted-foreground">
            Track sales orders, invoices and receipts by tournament
          </p>
        </div>
        <div className="w-full md:w-64">
          <TournamentSelector
            value={tournament}
            onChange={setTournament}
            className="w-full"
          />
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card data-testid="overview-contract-value">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Users2 className="h-4 w-4" /> Contract Value
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatINR(overview?.total_contract_value)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {overview?.sales_order_count || 0} Sales Orders
            </p>
          </CardContent>
        </Card>
        <Card data-testid="overview-invoiced">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <FileText className="h-4 w-4" /> Invoiced
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatINR(overview?.total_invoiced)}
            </div>
          </CardContent>
        </Card>
        <Card data-testid="overview-received">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Wallet className="h-4 w-4 text-green-600" /> Received (taxable)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatINR(receivedBreakdown.taxable)}
            </div>
            <div className="mt-1 text-[11px] text-muted-foreground leading-snug">
              TDS {formatINR(receivedBreakdown.tds)}
              <span className="mx-1.5 text-muted-foreground/50">·</span>
              GST {formatINR(receivedBreakdown.gst)}
            </div>
          </CardContent>
        </Card>
        <Card data-testid="overview-pending-invoicing">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600" /> Pending Invoicing
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">
              {formatINR(overview?.pending_invoicing)}
            </div>
          </CardContent>
        </Card>
        <Card data-testid="overview-pending-collection">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-red-600" /> Outstanding
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {formatINR(overview?.pending_collection)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* iPhone-style collection progress: how much of the total contract
          value has actually been received vs still owed to us. Green fill
          for received, neutral track for the rest. */}
      {(() => {
        const totalToReceive = Number(overview?.total_contract_value || 0);
        const received = Number(receivedBreakdown.taxable || overview?.total_received || 0);
        const pendingInv = Number(overview?.pending_invoicing || 0);
        const outstanding = Number(overview?.pending_collection || 0);
        const pctReceived =
          totalToReceive > 0 ? Math.min(100, (received / totalToReceive) * 100) : 0;
        return (
          <Card data-testid="revenue-collection-progress">
            <CardContent className="pt-6 pb-5 space-y-3">
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-sm font-semibold">Collection progress</div>
                  <div className="text-xs text-muted-foreground">
                    Received vs Total to Receive across all sales orders
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-muted-foreground">
                    Received {formatINR(received)} of {formatINR(totalToReceive)}
                  </div>
                  <div className="text-sm font-semibold text-emerald-600 tabular-nums">
                    {pctReceived.toFixed(1)}%
                  </div>
                </div>
              </div>
              <div
                className="w-full rounded-full overflow-hidden bg-muted/60"
                style={{ height: 12 }}
                data-testid="revenue-collection-bar"
              >
                <div
                  className="h-full rounded-full transition-[width] duration-700 ease-out bg-emerald-500"
                  style={{ width: `${pctReceived}%` }}
                />
              </div>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div className="flex items-center justify-between bg-emerald-50 border border-emerald-100 rounded px-3 py-2">
                  <span className="text-emerald-700">Received</span>
                  <span className="font-semibold tabular-nums">{formatINR(received)}</span>
                </div>
                <div className="flex items-center justify-between bg-amber-50 border border-amber-100 rounded px-3 py-2">
                  <span className="text-amber-700">Pending Invoicing</span>
                  <span className="font-semibold tabular-nums">{formatINR(pendingInv)}</span>
                </div>
                <div className="flex items-center justify-between bg-red-50 border border-red-100 rounded px-3 py-2">
                  <span className="text-red-700">Outstanding</span>
                  <span className="font-semibold tabular-nums">{formatINR(outstanding)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Upcoming Tentative Dates is rendered inside the Team Overview tab so the
          team summary boxes appear first when the user opens the page. The block
          is built once in a variable and inserted in two places. */}
      {(() => null)()}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 max-w-2xl">
          <TabsTrigger value="team-overview" data-testid="tab-team-overview">
            Team Overview
          </TabsTrigger>
          <TabsTrigger value="sales-orders" data-testid="tab-sales-orders">
            Sales Orders
          </TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">
            Invoices
          </TabsTrigger>
          <TabsTrigger value="receipts" data-testid="tab-receipts">
            Receipts
          </TabsTrigger>
        </TabsList>

        {/* ============== TEAM OVERVIEW TAB ============== */}
        <TabsContent value="team-overview" className="space-y-4 mt-4">
          {salesOrders.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No sales orders yet. Add one to see progress.
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Toolbar: search + filter + sort */}
              <Card>
                <CardContent className="py-3">
                  <div className="flex flex-col md:flex-row md:items-center gap-3">
                    <Input
                      data-testid="team-search-input"
                      placeholder="Search by team or SO number..."
                      value={teamSearch}
                      onChange={(e) => setTeamSearch(e.target.value)}
                      className="md:max-w-xs"
                    />
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-muted-foreground whitespace-nowrap">Filter</Label>
                      <Select value={teamFilter} onValueChange={setTeamFilter}>
                        <SelectTrigger className="w-40" data-testid="team-filter-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All teams</SelectItem>
                          <SelectItem value="behind">Behind plan</SelectItem>
                          <SelectItem value="on_track">On track</SelectItem>
                          <SelectItem value="outstanding">Has outstanding</SelectItem>
                          <SelectItem value="cleared">Fully cleared</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2 md:ml-auto">
                      <Label className="text-xs text-muted-foreground whitespace-nowrap">Sort by</Label>
                      <Select value={teamSort} onValueChange={setTeamSort}>
                        <SelectTrigger className="w-44" data-testid="team-sort-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="outstanding">Outstanding (high → low)</SelectItem>
                          <SelectItem value="contract">Contract value (high → low)</SelectItem>
                          <SelectItem value="team">Team name (A → Z)</SelectItem>
                          <SelectItem value="billing">% Billed (low → high)</SelectItem>
                          <SelectItem value="collection">% Collected (low → high)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="text-xs text-muted-foreground whitespace-nowrap">
                      {filteredSortedSOs.length} of {salesOrders.length}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleExportTeamOverview}
                      disabled={teamPdfExporting}
                      data-testid="team-overview-export-pdf"
                      data-html2canvas-ignore="true"
                    >
                      <Download className="h-4 w-4 mr-1.5" />
                      {teamPdfExporting ? 'Preparing…' : 'Export PDF'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
              {filteredSortedSOs.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center text-muted-foreground">
                    No teams match your filter.
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="team-overview-grid" ref={teamOverviewRef}>
                  {filteredSortedSOs.map((so) => {
                const soInvoices = invoices.filter((i) => i.sales_order_id === so.id);
                const soInvoiceIds = soInvoices.map((i) => i.id);
                const soReceipts = receipts.filter((r) => soInvoiceIds.includes(r.invoice_id));

                const total = so.total_amount || 0;
                const invoiced = so.invoiced_amount || 0;
                const received = so.received_amount || 0;
                const milestones = so.tentative_dates || [];

                // Like-to-like (taxable) basis: strips GST out of all comparisons so
                // SO contract, invoiced, received and milestone plan are apples-to-apples.
                const taxableContract = so.taxable_amount || total;
                const taxableInvoiced = soInvoices.reduce(
                  (s, i) => s + (i.taxable_amount || i.amount || 0),
                  0
                );
                const taxableReceived = soReceipts.reduce(
                  (s, r) => s + (r.taxable_amount || r.amount || 0),
                  0
                );

                // Milestone plan: total of all planned milestone amounts
                const planTotal = milestones.reduce(
                  (sum, m) => sum + (parseFloat(m.amount) || 0),
                  0
                );
                // Milestones due by today (cumulative amount that SHOULD have been hit per plan)
                const today = new Date();
                today.setHours(23, 59, 59, 999);
                const dueByToday = milestones.reduce((sum, m) => {
                  if (!m.date) return sum;
                  const d = new Date(m.date);
                  return d <= today ? sum + (parseFloat(m.amount) || 0) : sum;
                }, 0);

                // Per-milestone status: hit if cumulative receipts (sorted by date) >= cumulative milestone amount by that milestone's date
                const sortedMilestones = [...milestones]
                  .filter((m) => m.date)
                  .sort((a, b) => new Date(a.date) - new Date(b.date));

                const milestoneRows = sortedMilestones.map((m, idx) => {
                  const cumulativePlan = sortedMilestones
                    .slice(0, idx + 1)
                    .reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
                  const milestoneDate = new Date(m.date);
                  const receivedByDate = soReceipts.reduce((s, r) => {
                    return new Date(r.receipt_date) <= milestoneDate
                      ? s + (parseFloat(r.amount) || 0)
                      : s;
                  }, 0);
                  const hit = receivedByDate >= cumulativePlan && cumulativePlan > 0;
                  const overdue = !hit && milestoneDate <= today;
                  return {
                    ...m,
                    idx,
                    hit,
                    overdue,
                    cumulativePlan,
                    receivedByDate,
                  };
                });
                const hitCount = milestoneRows.filter((m) => m.hit).length;

                // Denominators:
                //  • Milestone bar uses the milestone plan (= what should be invoiced/received by today vs plan).
                //  • Invoiced/Received bars use the full taxable contract value (= overall billing/collection
                //    progress against the contract, regardless of how the milestones were planned).
                const milestoneDenom = planTotal > 0 ? planTotal : taxableContract;
                const contractDenom = taxableContract;

                const milestonePct = milestoneDenom > 0 ? Math.min(100, (dueByToday / milestoneDenom) * 100) : 0;
                const invoicedFromPlanPct = contractDenom > 0 ? Math.min(100, (taxableInvoiced / contractDenom) * 100) : 0;
                const receivedFromPlanPct = contractDenom > 0 ? Math.min(100, (taxableReceived / contractDenom) * 100) : 0;

                const onTrack = taxableReceived >= dueByToday;

                return (
                  <Card
                    key={so.id}
                    data-testid={`team-progress-${so.id}`}
                    className="border-border hover:shadow-md transition-shadow"
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <CardTitle className="font-heading text-base truncate">
                              {so.team_name}
                            </CardTitle>
                            {statusBadge(so.status)}
                          </div>
                          <CardDescription className="text-xs mt-0.5">
                            {so.so_number} · {formatINR(total)}
                          </CardDescription>
                        </div>
                        {milestones.length > 0 && (
                          <Badge
                            variant={onTrack ? 'success' : 'secondary'}
                            className="shrink-0"
                            data-testid={`on-track-${so.id}`}
                          >
                            {onTrack ? 'On track' : 'Behind plan'}
                          </Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Milestone Plan Bar */}
                      <div>
                        <div className="flex items-center justify-between text-[11px] mb-1.5">
                          <span className="font-medium text-foreground/80">
                            Milestone (plan)
                            {milestones.length > 0 && (
                              <span className="text-muted-foreground ml-1">
                                · {hitCount}/{milestoneRows.length} hit
                              </span>
                            )}
                          </span>
                          <span className="font-mono font-semibold text-foreground/80">
                            {milestonePct.toFixed(0)}%
                          </span>
                        </div>
                        <HoverCard openDelay={120}>
                          <HoverCardTrigger asChild>
                            <div
                              className="relative h-3.5 w-full rounded-full border border-border bg-muted/40 overflow-hidden cursor-pointer"
                              data-testid={`milestone-bar-${so.id}`}
                            >
                              <div
                                className="absolute inset-y-0 left-0 bg-foreground/70 transition-all duration-500 ease-out"
                                style={{ width: `${milestonePct}%` }}
                              />
                            </div>
                          </HoverCardTrigger>
                          <HoverCardContent className="w-80" align="start">
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="font-heading font-semibold text-sm">
                                  Milestone plan
                                </span>
                                <Badge variant="outline">
                                  {milestoneRows.length} milestone{milestoneRows.length !== 1 ? 's' : ''}
                                </Badge>
                              </div>
                              <div className="border-t pt-2 space-y-1 text-xs">
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Total plan</span>
                                  <span className="font-mono">{formatINR(planTotal)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Due by today</span>
                                  <span className="font-mono font-semibold">{formatINR(dueByToday)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Received so far</span>
                                  <span
                                    className={`font-mono font-semibold ${
                                      received >= dueByToday ? 'text-primary' : 'text-destructive'
                                    }`}
                                  >
                                    {formatINR(received)}
                                  </span>
                                </div>
                              </div>
                              {milestoneRows.length > 0 ? (
                                <div className="border-t pt-2 space-y-1 max-h-44 overflow-y-auto">
                                  {milestoneRows.map((m) => (
                                    <div
                                      key={m.idx}
                                      className="flex justify-between items-center text-xs"
                                    >
                                      <div className="flex flex-col">
                                        <span className="font-medium">{formatDate(m.date)}</span>
                                        {m.description && (
                                          <span className="text-muted-foreground text-[10px]">
                                            {m.description}
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="font-mono">{formatINR(m.amount)}</span>
                                        {m.hit ? (
                                          <Badge variant="success" className="text-[9px] px-1.5 py-0">Hit</Badge>
                                        ) : m.overdue ? (
                                          <Badge variant="destructive" className="text-[9px] px-1.5 py-0">Overdue</Badge>
                                        ) : (
                                          <Badge variant="outline" className="text-[9px] px-1.5 py-0">Upcoming</Badge>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-xs text-muted-foreground pt-2 border-t">
                                  No milestones defined. Add tentative dates on the Sales Order.
                                </div>
                              )}
                            </div>
                          </HoverCardContent>
                        </HoverCard>
                      </div>

                      {/* Invoiced bar removed per user feedback — kept the
                          taxable-invoiced number on the Received hover card so
                          users can still see invoicing-vs-receipts breakdown. */}

                      {/* Received Bar */}
                      <div>
                        <div className="flex items-center justify-between text-[11px] mb-1.5">
                          <span className="font-medium text-foreground/80">
                            Received <span className="text-muted-foreground/70">(taxable)</span>
                          </span>
                          <span className="font-mono font-semibold text-primary">
                            {receivedFromPlanPct.toFixed(0)}%
                            <span className="text-muted-foreground ml-1.5 font-normal">
                              {formatINR(taxableReceived)}
                            </span>
                          </span>
                        </div>
                        <HoverCard openDelay={120}>
                          <HoverCardTrigger asChild>
                            <div
                              className="relative h-3.5 w-full rounded-full border border-border bg-muted/40 overflow-hidden cursor-pointer"
                              data-testid={`received-bar-${so.id}`}
                            >
                              <div
                                className="absolute inset-y-0 left-0 bg-primary transition-all duration-500 ease-out"
                                style={{ width: `${receivedFromPlanPct}%` }}
                              />
                            </div>
                          </HoverCardTrigger>
                          <HoverCardContent className="w-80" align="start">
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="font-heading font-semibold text-sm">Receipts</span>
                                <Badge variant="outline">
                                  {soReceipts.length} receipt{soReceipts.length !== 1 ? 's' : ''}
                                </Badge>
                              </div>
                              <div className="border-t pt-2 space-y-1 text-xs">
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Invoiced (taxable)</span>
                                  <span className="font-mono">{formatINR(taxableInvoiced)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Received (taxable)</span>
                                  <span className="font-mono text-primary font-semibold">
                                    {formatINR(taxableReceived)}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Outstanding</span>
                                  <span
                                    className={`font-mono ${
                                      taxableInvoiced - taxableReceived > 0 ? 'text-destructive' : ''
                                    }`}
                                  >
                                    {formatINR(taxableInvoiced - taxableReceived)}
                                  </span>
                                </div>
                              </div>
                              {soReceipts.length > 0 && (
                                <div className="border-t pt-2 space-y-1 max-h-40 overflow-y-auto">
                                  {soReceipts.map((r) => (
                                    <div
                                      key={r.id}
                                      className="flex justify-between items-center text-xs"
                                    >
                                      <div className="flex flex-col">
                                        <span className="font-medium">{r.receipt_number}</span>
                                        <span className="text-muted-foreground text-[10px]">
                                          {formatDate(r.receipt_date)} · {r.payment_method}
                                        </span>
                                      </div>
                                      <span className="font-mono text-primary">
                                        {formatINR(r.taxable_amount || r.amount)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </HoverCardContent>
                        </HoverCard>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
                </div>
              )}

              {/* Upcoming Tentative Dates — moved here so team summary appears first */}
              {upcomingMilestones.length > 0 && (
                <Card data-testid="upcoming-milestones" data-html2canvas-ignore="true">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 font-heading">
                      <CalendarClock className="h-5 w-5 text-primary" />
                      Upcoming Tentative Dates
                    </CardTitle>
                    <CardDescription>Expected receipts / milestones coming up</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Team / SO</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {upcomingMilestones.map((m, i) => (
                          <TableRow key={`${m.so_id}-${m.idx}-${i}`}>
                            <TableCell className="font-medium">{formatDate(m.date)}</TableCell>
                            <TableCell>
                              <div className="font-medium">{m.team_name}</div>
                              <div className="text-xs text-muted-foreground">{m.so_number}</div>
                            </TableCell>
                            <TableCell>{m.description || '-'}</TableCell>
                            <TableCell>{formatINR(m.amount)}</TableCell>
                            <TableCell>
                              {m.daysUntil < 0 ? (
                                <Badge variant="destructive">Overdue {Math.abs(m.daysUntil)}d</Badge>
                              ) : m.daysUntil === 0 ? (
                                <Badge variant="default">Today</Badge>
                              ) : m.daysUntil <= 7 ? (
                                <Badge variant="secondary">In {m.daysUntil}d</Badge>
                              ) : (
                                <Badge variant="outline">In {m.daysUntil}d</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openReminderDialog(m)}
                                data-testid={`send-reminder-${m.so_id}-${m.idx}`}
                              >
                                <Mail className="h-3.5 w-3.5 mr-1" /> Remind
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* ============== SALES ORDERS TAB ============== */}
        <TabsContent value="sales-orders" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading">Sales Orders</CardTitle>
                <CardDescription>
                  Teams contracted for {tournament || 'current tournament'}
                </CardDescription>
              </div>
              <Button
                onClick={() => openSODialog()}
                data-testid="add-sales-order-button"
                data-write-only
                disabled={!tournament}
              >
                <Plus className="h-4 w-4 mr-2" /> New Sales Order
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="py-12 text-center text-muted-foreground">
                  Loading...
                </div>
              ) : salesOrders.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground">
                  No sales orders yet. Add one to get started.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead></TableHead>
                      <TableHead>SO #</TableHead>
                      <TableHead>Team</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Invoiced</TableHead>
                      <TableHead>Received</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Milestones</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salesOrders.map((so) => {
                      const balance = (so.total_amount || 0) - (so.invoiced_amount || 0);
                      const isExpanded = expandedSO === so.id;
                      const soInvoices = invoices.filter((i) => i.sales_order_id === so.id);
                      return (
                        <Fragment key={so.id}>
                          <TableRow data-testid={`so-row-${so.id}`}>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  setExpandedSO(isExpanded ? null : so.id)
                                }
                              >
                                <ChevronRight
                                  className={`h-4 w-4 transition-transform ${
                                    isExpanded ? 'rotate-90' : ''
                                  }`}
                                />
                              </Button>
                            </TableCell>
                            <TableCell className="font-medium">
                              {so.so_number}
                            </TableCell>
                            <TableCell>{so.team_name}</TableCell>
                            <TableCell>{formatINR(so.total_amount)}</TableCell>
                            <TableCell>
                              {formatINR(so.invoiced_amount)}
                              <div className="text-xs text-muted-foreground">
                                Bal: {formatINR(balance)}
                              </div>
                            </TableCell>
                            <TableCell className="text-green-600">
                              {formatINR(so.received_amount)}
                            </TableCell>
                            <TableCell>{statusBadge(so.status)}</TableCell>
                            <TableCell>
                              {(so.tentative_dates || []).length}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openInvoiceDialog(so.id)}
                                  data-testid={`create-invoice-for-${so.id}`}
                                  title="Create Invoice"
                                >
                                  <FileText className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openSODialog(so)}
                                  data-testid={`edit-so-${so.id}`}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="ghost" size="sm">
                                      <Trash2 className="h-4 w-4 text-destructive" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Delete sales order?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        This cannot be undone. Sales orders with invoices cannot be deleted.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => deleteSO(so.id)}>
                                        Delete
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </TableCell>
                          </TableRow>
                          {isExpanded && (
                            <TableRow>
                              <TableCell colSpan={9} className="bg-muted/30 p-4">
                                <div className="space-y-4">
                                  {so.notes && (
                                    <div>
                                      <div className="text-xs font-medium text-muted-foreground mb-1">
                                        Notes
                                      </div>
                                      <div className="text-sm">{so.notes}</div>
                                    </div>
                                  )}
                                  {(so.tentative_dates || []).length > 0 && (
                                    <div>
                                      <div className="text-xs font-medium text-muted-foreground mb-2">
                                        Tentative Milestones
                                      </div>
                                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                        {so.tentative_dates.map((m, idx) => (
                                          <div
                                            key={`${m.date || 'd'}-${m.amount ?? 0}-${idx}`}
                                            className="bg-background p-3 rounded border text-sm"
                                          >
                                            <div className="font-medium">
                                              {formatDate(m.date)}
                                            </div>
                                            <div className="text-primary">
                                              {formatINR(m.amount)}
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                              {m.description || '-'}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  <div>
                                    <div className="text-xs font-medium text-muted-foreground mb-2">
                                      Invoices ({soInvoices.length})
                                    </div>
                                    {soInvoices.length === 0 ? (
                                      <div className="text-sm text-muted-foreground">
                                        No invoices raised yet
                                      </div>
                                    ) : (
                                      <Table>
                                        <TableHeader>
                                          <TableRow>
                                            <TableHead>Invoice #</TableHead>
                                            <TableHead>Date</TableHead>
                                            <TableHead>Total</TableHead>
                                            <TableHead>Received</TableHead>
                                            <TableHead>Status</TableHead>
                                          </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                          {soInvoices.map((inv) => (
                                            <TableRow key={inv.id}>
                                              <TableCell className="font-medium">
                                                {inv.invoice_number}
                                              </TableCell>
                                              <TableCell>{formatDate(inv.invoice_date)}</TableCell>
                                              <TableCell>{formatINR(inv.total_amount)}</TableCell>
                                              <TableCell className="text-green-600">
                                                {formatINR(inv.received_amount)}
                                              </TableCell>
                                              <TableCell>{statusBadge(inv.status)}</TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    )}
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============== INVOICES TAB ============== */}
        <TabsContent value="invoices" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading">Sales Invoices</CardTitle>
                <CardDescription>
                  Invoices raised against sales orders
                </CardDescription>
              </div>
              <Button
                onClick={() => openInvoiceDialog()}
                data-testid="add-invoice-button"
                data-write-only
                disabled={salesOrders.length === 0}
              >
                <Plus className="h-4 w-4 mr-2" /> New Invoice
              </Button>
            </CardHeader>
            <CardContent>
              {tournamentInvoices.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground">
                  No invoices yet.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>SO / Team</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Due</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Received</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tournamentInvoices.map((inv) => (
                      <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                        <TableCell className="font-medium">
                          {inv.invoice_number}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{inv.team_name}</div>
                          <div className="text-xs text-muted-foreground">
                            {inv.so_number}
                          </div>
                        </TableCell>
                        <TableCell>{formatDate(inv.invoice_date)}</TableCell>
                        <TableCell>{formatDate(inv.due_date)}</TableCell>
                        <TableCell>
                          {/* Net receivable = Taxable + GST − TDS. Shows the
                              actual cash the team is expected to remit, so the
                              status pill (Fully paid / Partial / …) doesn't
                              falsely mark TDS-deducted invoices as partial. */}
                          {formatINR(
                            (Number(inv.total_amount) || 0) -
                              (Number(inv.tds_amount) || 0)
                          )}
                          {Number(inv.tds_amount) > 0 && (
                            <div className="text-[10px] text-muted-foreground">
                              net of TDS {formatINR(Number(inv.tds_amount))}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-green-600">
                          {formatINR(inv.received_amount)}
                        </TableCell>
                        <TableCell>{statusBadge(inv.status)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openReceiptDialog(inv.id)}
                              data-testid={`add-receipt-for-${inv.id}`}
                              title="Record Receipt"
                            >
                              <ReceiptIcon className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`download-invoice-pdf-${inv.id}`}
                              onClick={async () => {
                                try {
                                  const res = await axios.get(
                                    `${API}/revenue/invoices/${inv.id}/export-pdf`,
                                    { withCredentials: true, responseType: 'blob' }
                                  );
                                  const blob = new Blob([res.data], { type: 'application/pdf' });
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `Invoice_${(inv.invoice_number || inv.id).replace(/\//g, '_')}.pdf`;
                                  document.body.appendChild(a);
                                  a.click();
                                  a.remove();
                                  window.URL.revokeObjectURL(url);
                                } catch (e) {
                                  toast.error('Failed to download invoice PDF');
                                }
                              }}
                              title="Download PDF"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openInvoiceDialog('', inv)}
                              data-testid={`edit-invoice-${inv.id}`}
                              title="Edit Invoice"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete invoice?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Invoices with receipts cannot be deleted.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => deleteInvoice(inv.id)}>
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============== RECEIPTS TAB ============== */}
        <TabsContent value="receipts" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading">Receipts</CardTitle>
                <CardDescription>Collections recorded against invoices</CardDescription>
              </div>
              <Button
                onClick={() => openReceiptDialog()}
                data-testid="add-receipt-button"
                data-write-only
                disabled={tournamentInvoices.length === 0}
              >
                <Plus className="h-4 w-4 mr-2" /> New Receipt
              </Button>
            </CardHeader>
            <CardContent>
              {tournamentReceipts.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground">
                  No receipts recorded.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Receipt #</TableHead>
                      <TableHead>Invoice</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Bank Txn</TableHead>
                      <TableHead>Remarks</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tournamentReceipts.map((r) => (
                      <TableRow
                        key={r.id}
                        id={`receipt-${r.id}`}
                        data-testid={`receipt-row-${r.id}`}
                        className={
                          highlightId === r.id ? 'bg-amber-50 ring-2 ring-amber-300' : ''
                        }
                      >
                        <TableCell className="font-medium">
                          {r.receipt_number}
                        </TableCell>
                        <TableCell>{r.invoice_number}</TableCell>
                        <TableCell>{formatDate(r.receipt_date)}</TableCell>
                        <TableCell>{r.payment_method}</TableCell>
                        <TableCell>{r.reference_number || '-'}</TableCell>
                        <TableCell className="text-green-600 font-medium">
                          {formatINR(r.amount)}
                        </TableCell>
                        <TableCell>
                          {r.bank_txn_id ? (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-emerald-100 text-emerald-800"
                              title="Linked to an imported bank statement transaction"
                              data-testid={`receipt-bank-txn-tag-${r.id}`}
                            >
                              <span className="h-1.5 w-1.5 rounded-full bg-emerald-600" />
                              Tagged
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {r.remarks || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openReceiptDialog('', r)}
                              data-testid={`edit-receipt-${r.id}`}
                              title="Edit Receipt"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete receipt?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    The amount will be reverted from the invoice and sales order.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => deleteReceipt(r.id)}>
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ============== SO DIALOG ============== */}
      <Dialog open={soDialogOpen} onOpenChange={setSoDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingSO ? 'Edit Sales Order' : 'Create Sales Order'}
            </DialogTitle>
            <DialogDescription>
              Team contracted for tournament <b>{tournament}</b>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitSO} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="so-number">SO Number *</Label>
                <Input
                  id="so-number"
                  data-testid="so-number-input"
                  value={soForm.so_number}
                  onChange={(e) => setSoForm({ ...soForm, so_number: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="team-name">Team Name *</Label>
                <Input
                  id="team-name"
                  data-testid="team-name-input"
                  value={soForm.team_name}
                  onChange={(e) => setSoForm({ ...soForm, team_name: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="so-category">Revenue Category *</Label>
                <Select
                  value={soForm.category || 'Sponsorship'}
                  onValueChange={(v) => setSoForm({ ...soForm, category: v })}
                >
                  <SelectTrigger id="so-category" data-testid="so-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Sponsorship">Sponsorship</SelectItem>
                    <SelectItem value="Ad Revenue">Ad Revenue</SelectItem>
                    <SelectItem value="Franchise Fee">Franchise Fee</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="total-amount">Taxable Amount (excl. GST) *</Label>
                <Input
                  id="total-amount"
                  data-testid="so-total-amount-input"
                  type="number"
                  step="0.01"
                  value={soForm.taxable_amount || soForm.total_amount}
                  onChange={(e) =>
                    setSoForm({ ...soForm, taxable_amount: e.target.value, total_amount: e.target.value })
                  }
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="so-status">Status</Label>
                <Select
                  value={soForm.status}
                  onValueChange={(v) => setSoForm({ ...soForm, status: v })}
                >
                  <SelectTrigger id="so-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="partially_invoiced">Partially Invoiced</SelectItem>
                    <SelectItem value="fully_invoiced">Fully Invoiced</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="rounded-md border border-border bg-muted/20 p-3 space-y-3" data-testid="so-tax-block">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Tax Breakdown (₹)
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="so-cgst" className="text-xs">CGST</Label>
                  <Input
                    id="so-cgst"
                    type="number"
                    step="0.01"
                    value={soForm.cgst}
                    onChange={(e) => setSoForm({ ...soForm, cgst: e.target.value })}
                    data-testid="so-cgst-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="so-sgst" className="text-xs">SGST</Label>
                  <Input
                    id="so-sgst"
                    type="number"
                    step="0.01"
                    value={soForm.sgst}
                    onChange={(e) => setSoForm({ ...soForm, sgst: e.target.value })}
                    data-testid="so-sgst-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="so-igst" className="text-xs">IGST</Label>
                  <Input
                    id="so-igst"
                    type="number"
                    step="0.01"
                    value={soForm.igst}
                    onChange={(e) => setSoForm({ ...soForm, igst: e.target.value })}
                    data-testid="so-igst-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="so-tds" className="text-xs">TDS (withheld)</Label>
                  <Input
                    id="so-tds"
                    type="number"
                    step="0.01"
                    value={soForm.tds}
                    onChange={(e) => setSoForm({ ...soForm, tds: e.target.value })}
                    data-testid="so-tds-input"
                  />
                </div>
              </div>
              {(() => {
                const taxable = parseFloat(soForm.taxable_amount || soForm.total_amount || 0) || 0;
                const cgst = parseFloat(soForm.cgst || 0) || 0;
                const sgst = parseFloat(soForm.sgst || 0) || 0;
                const igst = parseFloat(soForm.igst || 0) || 0;
                const tds = parseFloat(soForm.tds || 0) || 0;
                const gross = taxable + cgst + sgst + igst;
                const net = gross - tds;
                return (
                  <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs pt-1 border-t border-border/40">
                    <span className="text-muted-foreground">
                      Gross (incl. GST): <span className="font-semibold text-foreground">{formatINR(gross)}</span>
                    </span>
                    <span className="text-muted-foreground">
                      Net Receivable: <span className="font-semibold text-foreground">{formatINR(net)}</span>
                    </span>
                  </div>
                );
              })()}
            </div>
            <div className="space-y-2">
              <Label htmlFor="so-notes">Notes</Label>
              <Textarea
                id="so-notes"
                value={soForm.notes}
                onChange={(e) => setSoForm({ ...soForm, notes: e.target.value })}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Tentative Dates / Milestones</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addMilestone}
                  data-testid="add-milestone-button"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Milestone
                </Button>
              </div>
              {soForm.tentative_dates.length === 0 ? (
                <div className="text-sm text-muted-foreground p-3 bg-muted/30 rounded">
                  No milestones. Add expected receipt/invoice dates to get reminders.
                </div>
              ) : (
                <div className="space-y-2">
                  {soForm.tentative_dates.map((m, idx) => (
                    <div key={`m-${idx}-${m.date || ''}`} className="grid grid-cols-12 gap-2 items-end p-2 border rounded">
                      <div className="col-span-3 space-y-1">
                        <Label className="text-xs">Date</Label>
                        <Input
                          type="date"
                          value={m.date}
                          onChange={(e) => updateMilestone(idx, 'date', e.target.value)}
                          data-testid={`milestone-date-${idx}`}
                        />
                      </div>
                      <div className="col-span-3 space-y-1">
                        <Label className="text-xs">Amount (₹)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={m.amount}
                          onChange={(e) => updateMilestone(idx, 'amount', e.target.value)}
                          data-testid={`milestone-amount-${idx}`}
                        />
                      </div>
                      <div className="col-span-2 space-y-1">
                        <Label className="text-xs">Type</Label>
                        <Select
                          value={m.type || 'payment'}
                          onValueChange={(v) => updateMilestone(idx, 'type', v)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="payment">Payment</SelectItem>
                            <SelectItem value="invoice">Invoice</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-3 space-y-1">
                        <Label className="text-xs">Description</Label>
                        <Input
                          value={m.description || ''}
                          onChange={(e) => updateMilestone(idx, 'description', e.target.value)}
                          placeholder="e.g. Advance"
                        />
                      </div>
                      <div className="col-span-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeMilestone(idx)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSoDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-so-button">
                {editingSO ? 'Update' : 'Create'} Sales Order
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ============== INVOICE DIALOG ============== */}
      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingInvoice ? 'Edit Sales Invoice' : 'Create Sales Invoice'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitInvoice} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="inv-so">Sales Order *</Label>
              <Select
                value={invoiceForm.sales_order_id}
                onValueChange={(v) =>
                  setInvoiceForm({ ...invoiceForm, sales_order_id: v })
                }
                disabled={!!editingInvoice}
              >
                <SelectTrigger id="inv-so" data-testid="invoice-so-select">
                  <SelectValue placeholder="Select sales order" />
                </SelectTrigger>
                <SelectContent>
                  {salesOrders.map((so) => {
                    const bal = (so.total_amount || 0) - (so.invoiced_amount || 0);
                    return (
                      <SelectItem key={so.id} value={so.id}>
                        {so.so_number} — {so.team_name} (Bal: {formatINR(bal)})
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="inv-number">Invoice Number *</Label>
                <div className="flex gap-2">
                  <Input
                    id="inv-number"
                    data-testid="invoice-number-input"
                    value={invoiceForm.invoice_number}
                    onChange={(e) =>
                      setInvoiceForm({ ...invoiceForm, invoice_number: e.target.value })
                    }
                    required
                  />
                  {!editingInvoice && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid="invoice-number-suggest-button"
                      onClick={async () => {
                        const next = await suggestInvoiceNumber(invoiceForm.invoice_date);
                        if (next) {
                          setInvoiceForm((p) => ({ ...p, invoice_number: next }));
                          toast.success(`Suggested ${next}`);
                        }
                      }}
                    >
                      Suggest
                    </Button>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="inv-date">Invoice Date *</Label>
                <Input
                  id="inv-date"
                  type="date"
                  value={invoiceForm.invoice_date}
                  onChange={(e) =>
                    setInvoiceForm({ ...invoiceForm, invoice_date: e.target.value })
                  }
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="inv-amount">Taxable Amount (excl. GST) *</Label>
                <Input
                  id="inv-amount"
                  data-testid="invoice-amount-input"
                  type="number"
                  step="0.01"
                  value={invoiceForm.taxable_amount || invoiceForm.amount}
                  onChange={(e) =>
                    setInvoiceForm({
                      ...invoiceForm,
                      taxable_amount: e.target.value,
                      amount: e.target.value,
                    })
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="inv-due">Due Date</Label>
                <Input
                  id="inv-due"
                  type="date"
                  value={invoiceForm.due_date}
                  onChange={(e) =>
                    setInvoiceForm({ ...invoiceForm, due_date: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Quick GST split</Label>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 px-2 text-xs"
                    onClick={() => {
                      const t = parseFloat(invoiceForm.taxable_amount || invoiceForm.amount || 0) || 0;
                      const half = Math.round((t * 0.09) * 100) / 100;
                      setInvoiceForm({ ...invoiceForm, cgst: String(half), sgst: String(half), igst: '0' });
                    }}
                    title="9% CGST + 9% SGST"
                  >
                    18% Intra
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 px-2 text-xs"
                    onClick={() => {
                      const t = parseFloat(invoiceForm.taxable_amount || invoiceForm.amount || 0) || 0;
                      setInvoiceForm({
                        ...invoiceForm,
                        igst: String(Math.round((t * 0.18) * 100) / 100),
                        cgst: '0',
                        sgst: '0',
                      });
                    }}
                    title="18% IGST"
                  >
                    18% Inter
                  </Button>
                </div>
              </div>
            </div>
            <div className="rounded-md border border-border bg-muted/20 p-3 space-y-3" data-testid="inv-tax-block">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Tax Breakdown (₹)
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="inv-cgst" className="text-xs">CGST</Label>
                  <Input
                    id="inv-cgst"
                    type="number"
                    step="0.01"
                    value={invoiceForm.cgst}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, cgst: e.target.value })}
                    data-testid="inv-cgst-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="inv-sgst" className="text-xs">SGST</Label>
                  <Input
                    id="inv-sgst"
                    type="number"
                    step="0.01"
                    value={invoiceForm.sgst}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, sgst: e.target.value })}
                    data-testid="inv-sgst-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="inv-igst" className="text-xs">IGST</Label>
                  <Input
                    id="inv-igst"
                    type="number"
                    step="0.01"
                    value={invoiceForm.igst}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, igst: e.target.value })}
                    data-testid="inv-igst-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="inv-tds" className="text-xs">TDS (withheld)</Label>
                  <Input
                    id="inv-tds"
                    type="number"
                    step="0.01"
                    value={invoiceForm.tds}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, tds: e.target.value })}
                    data-testid="inv-tds-input"
                  />
                </div>
              </div>
              {(() => {
                const taxable = parseFloat(invoiceForm.taxable_amount || invoiceForm.amount || 0) || 0;
                const cgst = parseFloat(invoiceForm.cgst || 0) || 0;
                const sgst = parseFloat(invoiceForm.sgst || 0) || 0;
                const igst = parseFloat(invoiceForm.igst || 0) || 0;
                const tds = parseFloat(invoiceForm.tds || 0) || 0;
                const gross = taxable + cgst + sgst + igst;
                const net = gross - tds;
                return (
                  <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs pt-1 border-t border-border/40">
                    <span className="text-muted-foreground">
                      Invoice Total: <span className="font-semibold text-foreground">{formatINR(gross)}</span>
                    </span>
                    <span className="text-muted-foreground">
                      Net Receivable (after TDS): <span className="font-semibold text-foreground">{formatINR(net)}</span>
                    </span>
                  </div>
                );
              })()}
            </div>
            <div className="space-y-2">
              <Label htmlFor="inv-notes">Notes</Label>
              <Textarea
                id="inv-notes"
                value={invoiceForm.notes}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })}
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInvoiceDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-invoice-button">
                {editingInvoice ? 'Update Invoice' : 'Create Invoice'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ============== RECEIPT DIALOG ============== */}
      <Dialog open={receiptDialogOpen} onOpenChange={setReceiptDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingReceipt ? 'Edit Receipt' : 'Record Receipt'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitReceipt} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rec-invoice">Invoice *</Label>
              <Select
                value={receiptForm.invoice_id}
                onValueChange={(v) =>
                  setReceiptForm({ ...receiptForm, invoice_id: v })
                }
                disabled={!!editingReceipt}
              >
                <SelectTrigger id="rec-invoice" data-testid="receipt-invoice-select">
                  <SelectValue placeholder="Select invoice" />
                </SelectTrigger>
                <SelectContent>
                  {tournamentInvoices
                    .filter((i) => editingReceipt ? true : i.status !== 'paid')
                    .map((inv) => {
                      const bal = (inv.total_amount || 0) - (inv.received_amount || 0);
                      return (
                        <SelectItem key={inv.id} value={inv.id}>
                          {inv.invoice_number} — {inv.team_name} (Due: {formatINR(bal)})
                        </SelectItem>
                      );
                    })}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="rec-number">Receipt Number *</Label>
                <Input
                  id="rec-number"
                  data-testid="receipt-number-input"
                  value={receiptForm.receipt_number}
                  onChange={(e) =>
                    setReceiptForm({ ...receiptForm, receipt_number: e.target.value })
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rec-date">Receipt Date *</Label>
                <Input
                  id="rec-date"
                  type="date"
                  value={receiptForm.receipt_date}
                  onChange={(e) =>
                    setReceiptForm({ ...receiptForm, receipt_date: e.target.value })
                  }
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="rec-amount">Amount Received (₹) *</Label>
                <Input
                  id="rec-amount"
                  data-testid="receipt-amount-input"
                  type="number"
                  step="0.01"
                  value={receiptForm.amount}
                  onChange={(e) =>
                    setReceiptForm({ ...receiptForm, amount: e.target.value })
                  }
                  required
                />
                <p className="text-[10px] text-muted-foreground">
                  Gross actually credited (post-TDS if buyer deducted at source).
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="rec-method">Payment Method</Label>
                <Select
                  value={receiptForm.payment_method}
                  onValueChange={(v) =>
                    setReceiptForm({ ...receiptForm, payment_method: v })
                  }
                >
                  <SelectTrigger id="rec-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                    <SelectItem value="UPI">UPI</SelectItem>
                    <SelectItem value="Cheque">Cheque</SelectItem>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="rounded-md border border-border bg-muted/20 p-3 space-y-3" data-testid="rec-tax-block">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Tax Allocation (₹) — for like-to-like reconciliation against the invoice
              </div>
              <div className="grid grid-cols-5 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="rec-taxable" className="text-xs">Taxable</Label>
                  <Input
                    id="rec-taxable"
                    type="number"
                    step="0.01"
                    value={receiptForm.taxable_amount}
                    onChange={(e) => setReceiptForm({ ...receiptForm, taxable_amount: e.target.value })}
                    data-testid="rec-taxable-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="rec-cgst" className="text-xs">CGST</Label>
                  <Input
                    id="rec-cgst"
                    type="number"
                    step="0.01"
                    value={receiptForm.cgst}
                    onChange={(e) => setReceiptForm({ ...receiptForm, cgst: e.target.value })}
                    data-testid="rec-cgst-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="rec-sgst" className="text-xs">SGST</Label>
                  <Input
                    id="rec-sgst"
                    type="number"
                    step="0.01"
                    value={receiptForm.sgst}
                    onChange={(e) => setReceiptForm({ ...receiptForm, sgst: e.target.value })}
                    data-testid="rec-sgst-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="rec-igst" className="text-xs">IGST</Label>
                  <Input
                    id="rec-igst"
                    type="number"
                    step="0.01"
                    value={receiptForm.igst}
                    onChange={(e) => setReceiptForm({ ...receiptForm, igst: e.target.value })}
                    data-testid="rec-igst-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="rec-tds" className="text-xs">TDS</Label>
                  <Input
                    id="rec-tds"
                    type="number"
                    step="0.01"
                    value={receiptForm.tds}
                    onChange={(e) => setReceiptForm({ ...receiptForm, tds: e.target.value })}
                    data-testid="rec-tds-input"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rec-ref">Reference Number</Label>
              <Input
                id="rec-ref"
                value={receiptForm.reference_number}
                onChange={(e) =>
                  setReceiptForm({ ...receiptForm, reference_number: e.target.value })
                }
                placeholder="UTR / Cheque #"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rec-remarks">Remarks</Label>
              <Textarea
                id="rec-remarks"
                data-testid="receipt-remarks-input"
                value={receiptForm.remarks}
                onChange={(e) =>
                  setReceiptForm({ ...receiptForm, remarks: e.target.value })
                }
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setReceiptDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-receipt-button">
                {editingReceipt ? 'Update Receipt' : 'Record Receipt'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ============== REMINDER DIALOG ============== */}
      <Dialog open={reminderDialogOpen} onOpenChange={setReminderDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="reminder-dialog">
          <DialogHeader>
            <DialogTitle>Send Payment Reminder</DialogTitle>
            <DialogDescription>
              {reminderTarget && (
                <>
                  {reminderTarget.team_name} · {reminderTarget.so_number} ·{' '}
                  {formatINR(reminderTarget.amount)} due{' '}
                  {formatDate(reminderTarget.date)}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={sendReminder} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rem-email">Recipient Email *</Label>
              <Input
                id="rem-email"
                type="email"
                required
                placeholder="team@example.com"
                value={reminderEmail}
                onChange={(e) => setReminderEmail(e.target.value)}
                data-testid="reminder-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rem-msg">Custom message (optional)</Label>
              <Textarea
                id="rem-msg"
                rows={3}
                placeholder="This is a friendly reminder regarding the upcoming milestone payment..."
                value={reminderMessage}
                onChange={(e) => setReminderMessage(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setReminderDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={reminderSending}
                data-testid="submit-reminder-button"
              >
                <Mail className="h-4 w-4 mr-2" />
                {reminderSending ? 'Sending...' : 'Send Reminder'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Revenue;
